<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.groups', 2),
                        href: meta.links.group_index,
                    },
                    {
                        name: group.name,
                        href: meta.links.group_show,
                    },
                    {
                        name: $t('messages.efficiency_per_tag'),
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <ActivityMenuOptions>
                            <InertiaLink
                                v-for="otherGroup in meta.links.otherGroups"
                                :href="otherGroup.url"
                                :key="otherGroup.url"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ otherGroup.text }}
                            </InertiaLink>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <EmptyState v-if="subjects.length === 0" picture />
        <div v-else class="flex flex-wrap gap-x-2">
            <AppButton
                element="InertiaLink"
                :href="subject.url"
                v-for="subject in subjects"
                :key="subject.id"
                class="flex gap-x-2"
            >
                {{ subject.text }}

                <IconCircleCheckFilled class="h-5 w-5 text-indigo-600" v-if="filters.subject_id == subject.id" />
            </AppButton>
        </div>

        <div class="flex flex-wrap -space-x-1 relative z-0 overflow-hidden mt-3" v-if="filters.subject_id">
            <button
                v-for="student in options.students"
                :key="student.id"
                type="button"
                :title="student.text"
                :disabled="form.processing"
                @click="selectStudent(student)"
            >
                <img
                    :src="student.avatar_or_default"
                    class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                    :class="{ 'opacity-50': form.student_id != student.id }"
                    alt=""
                />
            </button>
        </div>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter" v-if="filters.subject_id">
            <div class="flex justify-end mt-5">
                <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                <div class="mt-1">
                    <SubmitFormButton :button-text="$t('messages.filter')" :processing="form.processing" is-filter />
                </div>
            </div>
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1" v-if="options.contentBlocks.length">
                        <FormLabel :value="$tc('messages.content_blocks', 1)" for="content_block_id" />
                        <FormSelect
                            id="content_block_id"
                            v-model="form.content_block_id"
                            :options="options.contentBlocks"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.content_block_id" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2" v-if="options.tags.length && filters.content_block_id">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.tags', 1)" for="tag_id" />
                        <FormSelect id="tag_id" v-model="form.tag_id" :options="options.tags" class="mt-1 w-full" />
                        <FormError :value="form.errors.tag_id" class="mt-2" />
                    </div>
                </div>
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.student')" for="student_id" />
                        <FormSelect
                            id="student_id"
                            v-model="form.student_id"
                            :options="options.students"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.student_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.activity_mode')" for="mode" />
                        <FormSelect
                            id="mode"
                            v-model="form.mode"
                            :options="options.modes"
                            class="mt-1 w-full"
                            :null-option-text="$t('messages.all')"
                        />
                        <FormError :value="form.errors.mode" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_from')" for="date_from">
                            <ContextMenu>
                                <ul>
                                    <li v-for="period in options.date_periods" :key="period.url" class="mt-1">
                                        <AppButton element="InertiaLink" class="block" :href="period.url" size="sm">
                                            {{ period.text }}
                                        </AppButton>
                                    </li>
                                </ul>
                            </ContextMenu>
                        </FormLabel>
                        <FormInput
                            id="date_from"
                            v-model="form.date_from"
                            type="date"
                            name="date_from"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.date_from" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date_to')" for="date_to" />
                        <FormInput id="date_to" v-model="form.date_to" type="date" name="date_to" class="mt-1 w-full" />
                        <FormError :value="form.errors.date_to" class="mt-2" />
                    </div>
                </div>
            </div>
        </form>

        <div v-if="graph">
            <div>
                <apexchart
                    :type="graph.chart.type"
                    :height="graph.chart.height"
                    :series="graph.series"
                    :options="graph"
                />
            </div>
        </div>

        <div class="flex justify-between mt-5">
            <div>
                <AppButton desired-action element="InertiaLink" :href="meta.links.previous" v-if="meta.links.previous">
                    {{ $t('messages.previous') }}
                </AppButton>
            </div>
            <div>
                <AppButton desired-action element="InertiaLink" :href="meta.links.next" v-if="meta.links.next">
                    {{ $t('messages.next') }}
                </AppButton>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import { IconCircleCheckFilled } from '@tabler/icons-vue';
import EmptyState from '@/components/EmptyState.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import ContextMenu from '@/components/ContextMenu.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        ContextMenu,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        EmptyState,
        IconCircleCheckFilled,
        ActivityMenuOptions,
        PanelPage,
        AppButton,
    },
    layout: Layout,
    name: 'Index',
    props: ['subjects', 'graph', 'group', 'filters', 'options', 'meta'],
    data() {
        return {
            form: this.$inertia.form(this.filters),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index, {
                preserveScroll: true,
            });
        },
        selectStudent(student) {
            if (student.id === +this.form.student_id) {
                this.form.student_id = null;
                this.filter();
                return;
            }
            this.form.student_id = student.id;
            this.filter();
        },
    },
};
</script>
